import { useEffect, useMemo, useRef, useState } from 'react'
import { salonService } from '../../../../_services/salon.service'
import { loadBrandKit, saveBrandKit, kitFromSalon, marketFor, detectLogoShape, KIT_KEYS } from '../email-composer/email/theme'
import { MARKET_LOOK } from '../email-composer/data/templates'
import { hydrateBrandKit, pushBrandKit, flushBrandKit } from './brandKitSync'

// The salon's brand, outside any one email: what Marketing > Config edits
// and the Campaigns hub checks. The same brand kit the email builder saves:
// kept on the salon record (emailBrandKit) and cached in localStorage
// (beu-email-brand-<salonId>) for instant, offline reads; see brandKitSync.
//
// With syncSalon, the logo and colour are also written to the salon record
// (Settings > Salon > Email branding), which booking emails already use, so
// there's one logo and one colour wherever they're set.

const salonIdNow = () => localStorage.getItem('salonId')

const readSalon = async (salonId) => {
  const res = await salonService.getSalonSettings(salonId)
  const d = res && res.data
  const rd = d && (d.responseData || d)
  return rd && rd.data && typeof rd.data === 'object' ? rd.data : null
}

// PUT salon/:id/settings with only the keys being changed (as setup does).
// A refusal can come back as HTTP 200, so the reply is checked.
const saveSalonFields = async (salonId, patch) => {
  const fd = new FormData()
  fd.append('salonId', salonId)
  Object.keys(patch).forEach((k) => fd.append(k, patch[k]))
  const res = await salonService.updateSalon(fd)
  const d = res && res.data
  const bad = !d || d.errorCode || Number(d.statusCode) === 0
    || (d.responseData && typeof d.responseData === 'object' && (d.responseData.errorCode || Number(d.responseData.statusCode) === 0))
  if (bad) throw new Error('not saved')
}

// The header image: one of the salon's own photos goes in emailHeaderImage
// (a file name in salon_images/, as Settings > Salon sets it); any other logo
// (e.g. uploaded through the marketing picker) goes in emailHeaderImageUrl,
// which booking emails prefer. Either way it stays out of the public gallery.
const salonImageKey = (url) => {
  const m = /\/salon_images\/([^/?#]+)/.exec(String(url || ''))
  return m ? decodeURIComponent(m[1]) : null
}

const kitFromSpec = (spec, salonKit) => {
  const kit = {}
  KIT_KEYS.forEach((k) => { if (spec[k] !== undefined && spec[k] !== null && spec[k] !== '') kit[k] = spec[k] })
  if (spec.look) kit.look = spec.look
  // A logo taken off stays off, even though Settings still has one
  if (!kit.logo && salonKit && salonKit.logo) kit.logo = null
  return kit
}

export const brandIsSet = (salonId = salonIdNow()) => {
  const kit = loadBrandKit(salonId)
  return !!(kit && Object.keys(kit).length)
}

export const useSalonBrand = ({ syncSalon = false } = {}) => {
  const salonId = salonIdNow()
  const [salon, setSalon] = useState(undefined)
  const [saved, setSaved] = useState(() => loadBrandKit(salonId))
  const [editing, setEditing] = useState(null)
  const [flash, setFlash] = useState(false)
  const [syncNote, setSyncNote] = useState(null)
  // Saves the salon made on this visit (not the quiet logo-shape ones)
  const [changes, setChanges] = useState(0)
  const r = useRef({ timer: null, flash: null, pending: null, synced: null, start: null, touched: {}, syncing: Promise.resolve() })

  useEffect(() => {
    if (!salonId) { setSalon(null); return undefined }
    let alive = true
    readSalon(salonId)
      .then((s) => {
        if (!alive) return
        setSalon(s)
        r.current.synced = s ? { accent: s.emailPrimaryColor || '', logo: s.emailHeaderImageUrl || '' } : null
        // The server's kit wins (and refreshes this device's cache); a kit
        // only this device has goes up once
        const kit = hydrateBrandKit(salonId, s)
        if (kit && !r.current.start) setSaved(kit)
      })
      .catch(() => { if (alive) setSalon(null) })
    return () => { alive = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Bailey saving the brand (from a chat, on any page) shows here straight away
  useEffect(() => {
    const again = () => { if (!r.current.pending) setSaved(loadBrandKit(salonId)) }
    window.addEventListener('beu-bailey-brand', again)
    return () => window.removeEventListener('beu-bailey-brand', again)
  }, [salonId])

  const salonKit = useMemo(() => (salon ? kitFromSalon(salon) : null), [salon])
  const market = salon ? marketFor(salon.businessType) : null
  const base = useMemo(() => {
    const k = { ...(salonKit || {}), ...(saved || {}) }
    return { ...k, look: k.look || MARKET_LOOK[market] || 'gloss' }
  }, [salonKit, saved, market])
  const spec = editing || base

  const syncToSalon = async (kit) => {
    const last = r.current.synced
    const start = r.current.start
    if (!syncSalon || !salonId || !last || !start) return
    // Only what was changed here: a logo that came from the salon photo
    // isn't pushed into booking emails because the colour changed
    const patch = {}
    const accent = kit.accent || ''
    if (r.current.touched.accent && accent !== last.accent) patch.emailPrimaryColor = accent
    const logo = kit.logo || ''
    if (r.current.touched.logo && logo !== last.logo) {
      const key = logo ? salonImageKey(logo) : ''
      if (key !== null) patch.emailHeaderImage = key
      else if (/^https:\/\//i.test(logo)) patch.emailHeaderImageUrl = logo
    }
    if (!Object.keys(patch).length) return
    await saveSalonFields(salonId, patch)
    r.current.synced = {
      accent: 'emailPrimaryColor' in patch ? accent : last.accent,
      logo: ('emailHeaderImage' in patch || 'emailHeaderImageUrl' in patch) ? logo : last.logo,
    }
    setSyncNote(null)
  }

  const write = (next) => {
    const kit = kitFromSpec(next, salonKit)
    setSaved(saveBrandKit(salonId, kit))
    pushBrandKit(salonId, kit)
    const syncing = syncToSalon(kit)
    r.current.syncing = syncing
    syncing.catch(() => {
      // Staff without salon settings rights, or offline: the brand is still
      // saved for emails, only booking emails keep their old look
      setSyncNote('Saved for your emails. Your booking emails couldn’t be updated just now.')
    })
  }

  // Everything edited so far, on the salon record now. Rejects when it
  // couldn't be saved, so nothing copies the brand from before.
  const flush = async () => {
    const c = r.current
    if (c.pending) {
      clearTimeout(c.timer)
      const next = c.pending
      c.pending = null
      write(next)
    }
    await c.syncing
    await flushBrandKit(salonId)
  }

  const update = (next, { quiet = false } = {}) => {
    const c = r.current
    if (!c.start && !quiet) c.start = { accent: spec.accent || '', logo: spec.logo || '' }
    if (c.start && (next.accent || '') !== c.start.accent) c.touched.accent = true
    if (c.start && (next.logo || '') !== c.start.logo) c.touched.logo = true
    setEditing(next)
    // A shape worked out for a brand nobody has set up yet only shows here;
    // it isn't the salon choosing a brand
    if (quiet && !(saved && Object.keys(saved).length) && !c.pending) return
    clearTimeout(c.timer)
    c.pending = next
    c.timer = setTimeout(() => {
      c.pending = null
      write(next)
      if (quiet) return
      setChanges((n) => n + 1)
      setFlash(true)
      clearTimeout(c.flash)
      c.flash = setTimeout(() => setFlash(false), 2200)
    }, 500)
  }

  // A new logo gets a shape from the picture itself (wide logos as they are,
  // square photos in a circle), once
  useEffect(() => {
    if (!spec.logo || spec.logoShapeFor === spec.logo) return undefined
    let alive = true
    const logo = spec.logo
    detectLogoShape(logo).then((shape) => {
      if (!alive) return
      update({ ...spec, logoShape: shape === 'natural' ? undefined : shape, logoShapeFor: logo }, { quiet: true })
    })
    return () => { alive = false }
  }, [spec.logo, spec.logoShapeFor]) // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving the page mid-change still saves it
  useEffect(() => () => {
    const c = r.current
    clearTimeout(c.timer)
    clearTimeout(c.flash)
    if (c.pending) write(c.pending)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    spec,
    salon,
    salonKit,
    loading: salon === undefined,
    isSet: !!(saved && Object.keys(saved).length),
    update,
    saved: flash,
    syncNote,
    changes,
    flush,
  }
}
